import { endOfMonth, endOfQuarter, endOfYear, sub } from 'date-fns';
import { useAtomValue } from 'jotai';
import { capitalize } from 'lodash-es';
import { useCallback } from 'react';
import { Temporal } from 'temporal-polyfill';
import {
  ICompanyFinancialsDataModel,
  KpiPeriod,
  KpiSection,
} from '../../../data-models/company-financials.data-model';
import { IField } from '../../../data-models/field2.data-model';
import { FieldDefinition } from '../../../data-models/field-definition/field-definition.schema';
import { COMPANY_VIEW_TYPE } from '../../../data-models/view-config.data-model';
import { customFieldsByIdMapAtom } from '../../../services/state/AdminPanel/CustomFieldsStateJ';
import { kpiDefinitionByKeyMapAtom } from '../../../services/state/KPIConfigState';
import { ColumnMeta } from '../../../types';
import { compareBySortOrder } from '../../../util/comparators';
import { dateToPlainDateString } from '../../../util/date-utilts';
import { getFormattedFiscalDate } from '../../CompanyProfiles/utils/financialUtils';
import { MONTHS } from '../../KPI/components/Sections/FormItem/KPITable/useKPITableMetricsInputs';
import { columnsByPathState } from '../state/ViewStateJ';
import {
  createMprViewSettings,
  IMprViewSettings,
  IMprViewSettingsV1,
  IMultiPeriodReportingCustomDataPoint,
  IMultiPeriodReportingDataPoint,
  IMultiPeriodReportingMetricsDataPoint,
  MprColumnConfig,
  MprColumnSortType,
  MprLayout,
  MprViewSettingsStored,
  ReportingPeriod,
  ReportingPeriodType,
} from './MultiPeriodReporting.schemas';
import { useRemoveSensitiveColumns } from './multiPeriodReportingPermUtils';

export function mprKpiColumnConfigToDataPoint(
  column: IMprKpiColumnConfig,
  kpisByKeyMap: Map<string, IField<unknown>>
): IMultiPeriodReportingDataPoint | null {
  const { kpiKey, period, section } = column;
  const date = getDateForConfig(column);
  const kpiId = kpisByKeyMap.get(kpiKey)?.id;
  if (!kpiId) {
    return null;
  }

  return {
    date,
    kpiId: kpiId!,
    period,
    section,
  };
}

export function mprMetricsColumnConfigsToDataPoints(
  columns: IMprMetricsColumnConfig[],
  columnsByPath: Map<string, ColumnMeta>
): IMultiPeriodReportingMetricsDataPoint[] {
  const columnsByDate = columns.reduce((map, col) => {
    const date = getDateForConfig(col);
    if (!map.has(date)) {
      map.set(date, []);
    }
    const colId = columnsByPath.get(col.columnPath)?.id;
    if (!colId) {
      return map;
    }
    map.get(date)!.push(colId);
    return map;
  }, new Map<string, number[]>());

  return Array.from(columnsByDate.entries()).map(([date, colIds]) => ({
    date,
    columnIds: colIds,
  }));
}

export function mprCustomFieldColumnConfigsToDataPoints(
  columns: IMprCustomFieldColumnConfig[]
): IMultiPeriodReportingCustomDataPoint[] {
  const columnsByDate = columns.reduce((map, col) => {
    const date = getDateForConfig(col);
    if (!map.has(date)) {
      map.set(date, []);
    }
    map.get(date)!.push(col.customFieldId);
    return map;
  }, new Map<string, number[]>());

  return Array.from(columnsByDate.entries()).map(([date, customFieldIds]) => ({
    date,
    customFieldIds,
  }));
}

export function getDateForConfig(columnConfig: IAbstractMprColumnConfig): string {
  const type = columnConfig.type;

  let date;
  if (type === ReportingPeriodType.relative) {
    date = getDateStrForRelativePeriod(columnConfig.period as KpiPeriod, columnConfig.relativeDistance ?? 0);
  } else if (type === ReportingPeriodType.absolute) {
    date = getDateStrForAbsolutePeriod(columnConfig.year!, columnConfig.month as number | undefined);
  } else {
    throw new Error('Invalid type');
  }
  return date;
}

export function getDateStrForRelativePeriod(period: KpiPeriod, relativeDistance: number): string {
  let date;
  if (period === KpiPeriod.quarter) {
    date = endOfQuarter(sub(new Date(), { months: (relativeDistance! + 1) * 3 }));
  } else if (period === KpiPeriod.year) {
    date = endOfYear(sub(new Date(), { years: (relativeDistance! + 1) * 1 }));
  } else {
    date = endOfMonth(sub(new Date(), { months: (relativeDistance! + 1) * 1 }));
  }
  return dateToPlainDateString(date);
}

export function getDateStrForAbsolutePeriod(year: number, month: number = 12): string {
  const date = Temporal.PlainYearMonth.from(`${year}-${String(month).padStart(2, '0')}`);
  return date.toPlainDate({ day: date.daysInMonth }).toString();
}

export interface IAbstractMprColumnConfig extends Pick<ICompanyFinancialsDataModel, 'period'> {
  type: ReportingPeriod;
  month?: number | null;
  sortOrder?: number;
  relativeDistance?: number | null;
  year?: number | null;
}

export interface IMprKpiColumnConfig
  extends Pick<ICompanyFinancialsDataModel, 'section'>, IAbstractMprColumnConfig {
  kpiKey: string;
}
export function isMprKpiColumnConfig(config: IAbstractMprColumnConfig): config is IMprKpiColumnConfig {
  return 'kpiKey' in config;
}

export interface IMprMetricsColumnConfig extends IAbstractMprColumnConfig {
  columnPath: string;
}
export function isMprMetricsColumnConfig(
  config: IAbstractMprColumnConfig
): config is IMprMetricsColumnConfig {
  return 'columnPath' in config;
}

export interface IMprCustomFieldColumnConfig extends IAbstractMprColumnConfig {
  customFieldId: number;
}
export function isMprCustomFieldColumnConfig(
  config: IAbstractMprColumnConfig
): config is IMprCustomFieldColumnConfig {
  return 'customFieldId' in config;
}
export function createAbstractMprColumnConfig(
  overrides: Partial<IAbstractMprColumnConfig> = {}
): IAbstractMprColumnConfig {
  return {
    period: KpiPeriod.quarter,
    type: ReportingPeriodType.relative,
    relativeDistance: 0,
    ...overrides,
  };
}
export function createMprKpiColumnConfig(overrides: Partial<IMprKpiColumnConfig> = {}): IMprKpiColumnConfig {
  return {
    ...createAbstractMprColumnConfig(),
    kpiKey: 'kpiKey',
    section: KpiSection.actual,
    ...overrides,
  };
}

export function createMprMetricsColumnConfig(
  overrides: Partial<IMprMetricsColumnConfig> = {}
): IMprMetricsColumnConfig {
  return {
    ...createAbstractMprColumnConfig(),
    columnPath: 'columnPath',
    ...overrides,
  };
}

export function createMprCustomFieldColumnConfig(
  overrides: Partial<IMprCustomFieldColumnConfig> = {}
): IMprCustomFieldColumnConfig {
  return {
    ...createAbstractMprColumnConfig(),
    customFieldId: 0,
    ...overrides,
  };
}
// used for colIds and to compare configs
export function getColumnConfigKey(config: MprColumnConfig): string {
  const { period, type, relativeDistance, year, month } = config;
  if (isMprKpiColumnConfig(config)) {
    const { kpiKey, section } = config;
    return JSON.stringify({ kpiKey, section, period, type, relativeDistance, year, month });
  } else if (isMprMetricsColumnConfig(config)) {
    const { columnPath } = config;
    return JSON.stringify({ columnPath, period, type, relativeDistance, year, month });
  } else {
    // isMprCustomFieldColumnConfig
    const { customFieldId } = config;
    return JSON.stringify({ customFieldId, period, type, relativeDistance, year, month });
  }
}

export const LastFullPeriodPrefix = 'Last Full';
export function getFormattedConfigPeriod(config: IAbstractMprColumnConfig) {
  const { type, period, relativeDistance, year, month } = config;
  if (type == ReportingPeriodType.relative) {
    if (relativeDistance === 0) {
      return `${LastFullPeriodPrefix} ${period}`;
    } else {
      return `${LastFullPeriodPrefix} ${period} - ${relativeDistance}`;
    }
  } else if (type == ReportingPeriodType.absolute) {
    if (period === KpiPeriod.year) {
      return `${year}`;
    } else if (period === KpiPeriod.quarter) {
      return `Q${month! / 3} ${year}`;
    } else if (period === KpiPeriod.month) {
      return `${MONTHS[month! - 1]} ${year}`;
    }
  }
  return '';
}

/** What a period means today: "Q3 2026", "2025", "Mar 2026". */
export function getResolvedMprPeriod(period: MprPeriod): string {
  return getFormattedFiscalDate(getDateForConfig(period), period.period as KpiPeriod, 12);
}

/** A period's column header: "Last Full Quarter (Q3 2026)" for a rolling period, "Q2 2025" for a fixed one. */
export function getMprPeriodHeader(period: MprPeriod): string {
  const resolved = getResolvedMprPeriod(period);
  return period.type === ReportingPeriodType.relative
    ? `${getFormattedConfigPeriod(period)} (${resolved})`
    : resolved;
}

/** An item's column header: a KPI carries its section, "Revenue · Actual". */
export function formatMprItemHeader(name: string, item: MprItem): string {
  return isMprKpiItem(item) ? `${name} · ${capitalize(item.section)}` : name;
}

export const PeriodToSortOrder: Record<KpiPeriod, number> = {
  [KpiPeriod.month]: 1,
  [KpiPeriod.quarter]: 2,
  [KpiPeriod.year]: 3,
};

/** Oldest first by date, then shorter periods first: the rows layout's column order. */
export function mprChronologicalComparator(a: IAbstractMprColumnConfig, b: IAbstractMprColumnConfig) {
  return (
    getDateForConfig(a).localeCompare(getDateForConfig(b)) ||
    PeriodToSortOrder[a.period as KpiPeriod] - PeriodToSortOrder[b.period as KpiPeriod]
  );
}

export function multiPeriodDateComparator(a: IAbstractMprColumnConfig, b: IAbstractMprColumnConfig) {
  const aDate = getDateForConfig(a);
  const bDate = getDateForConfig(b);
  const aPeriod = a.period;
  const bPeriod = b.period;
  if (aPeriod === bPeriod) {
    return aDate.localeCompare(bDate);
  } else {
    return PeriodToSortOrder[aPeriod as KpiPeriod] - PeriodToSortOrder[bPeriod as KpiPeriod];
  }
}

export function getMprItemDisplayName(
  item: MprItem,
  kpiDefinitionsByKey: Map<string, FieldDefinition>,
  columnsByPath: Map<string, ColumnMeta>,
  customFieldsById: Map<number, IField<unknown>>
): string {
  if (isMprKpiItem(item)) {
    return kpiDefinitionsByKey.get(item.kpiKey)?.displayName ?? '';
  }
  if (isMprMetricsItem(item)) {
    return columnsByPath.get(item.columnPath)?.displayName ?? '';
  }
  return customFieldsById.get(item.customFieldId)?.displayName ?? '';
}

export function useMprItemDisplayName() {
  const kpiDefinitionsByKey = useAtomValue(kpiDefinitionByKeyMapAtom);
  const columnsByPath = useAtomValue(columnsByPathState(COMPANY_VIEW_TYPE.RETURN_FORECAST));
  // Custom-field lookup stays on the legacy id-keyed atom: the saved MPR view
  // stores `customFieldId` (numeric BE id), and `FieldDefinition.key` is the
  // string `entityField`.
  const customFieldsById = useAtomValue(customFieldsByIdMapAtom);

  return useCallback(
    (item: MprItem) => getMprItemDisplayName(item, kpiDefinitionsByKey, columnsByPath, customFieldsById),
    [kpiDefinitionsByKey, columnsByPath, customFieldsById]
  );
}

/** Brings a saved view's settings up to date and drops what this user may not see. */
export function usePreprocessMprViewSettings() {
  const itemDisplayName = useMprItemDisplayName();
  const removeSensitiveColumns = useRemoveSensitiveColumns();

  return useCallback(
    (stored: MprViewSettingsStored): IMprViewSettings => {
      const settings = migrateMprViewSettings(stored, itemDisplayName);
      return { ...settings, items: removeSensitiveColumns(settings.items) };
    },
    [itemDisplayName, removeSensitiveColumns]
  );
}

// ---------- items and periods ----------
// A column is one item reported for one period; the view stores the two lists and the
// columns are derived (see getMprViewColumns).

export type MprPeriod = Pick<
  IAbstractMprColumnConfig,
  'period' | 'type' | 'month' | 'year' | 'relativeDistance'
>;
export type MprKpiItem = Pick<IMprKpiColumnConfig, 'kpiKey' | 'section'>;
export type MprMetricsItem = Pick<IMprMetricsColumnConfig, 'columnPath'>;
export type MprCustomFieldItem = Pick<IMprCustomFieldColumnConfig, 'customFieldId'>;
export type MprItem = MprKpiItem | MprMetricsItem | MprCustomFieldItem;

export function isMprKpiItem(item: MprItem): item is MprKpiItem {
  return 'kpiKey' in item;
}
export function isMprMetricsItem(item: MprItem): item is MprMetricsItem {
  return 'columnPath' in item;
}
export function isMprCustomFieldItem(item: MprItem): item is MprCustomFieldItem {
  return 'customFieldId' in item;
}

export function getMprItemKey(item: MprItem): string {
  if (isMprKpiItem(item)) {
    return JSON.stringify({ kpiKey: item.kpiKey, section: item.section });
  }
  if (isMprMetricsItem(item)) {
    return JSON.stringify({ columnPath: item.columnPath });
  }
  return JSON.stringify({ customFieldId: item.customFieldId });
}

/** The month a fixed year ends in; a year period is the same period with or without it. */
export const YearEndMonth = 12;

export function getMprPeriodKey({ period, type, relativeDistance, year, month }: MprPeriod): string {
  const fixedYear = type === ReportingPeriodType.absolute && period === KpiPeriod.year;
  return JSON.stringify({
    period,
    type,
    relativeDistance: relativeDistance ?? null,
    year: year ?? null,
    month: month ?? (fixedYear ? YearEndMonth : null),
  });
}

export function splitMprColumnConfig(column: MprColumnConfig): { item: MprItem; period: MprPeriod } {
  const { period, type, month, year, relativeDistance } = column;
  const periodPart: MprPeriod = { period, type };
  if (type === ReportingPeriodType.relative) {
    periodPart.relativeDistance = relativeDistance ?? 0;
  } else {
    periodPart.year = year;
    const endMonth = month ?? (period === KpiPeriod.year ? YearEndMonth : null);
    if (endMonth != null) {
      periodPart.month = endMonth;
    }
  }
  if (isMprKpiColumnConfig(column)) {
    return { item: { kpiKey: column.kpiKey, section: column.section }, period: periodPart };
  }
  if (isMprMetricsColumnConfig(column)) {
    return { item: { columnPath: column.columnPath }, period: periodPart };
  }
  return { item: { customFieldId: column.customFieldId }, period: periodPart };
}

export function toMprColumnConfig(item: MprItem, period: MprPeriod, sortOrder?: number): MprColumnConfig {
  return { ...period, ...item, ...(sortOrder != null ? { sortOrder } : {}) } as MprColumnConfig;
}

export function uniqueMprItems(items: MprItem[]): MprItem[] {
  return uniqueBy(items, getMprItemKey);
}
export function uniqueMprPeriods(periods: MprPeriod[]): MprPeriod[] {
  return uniqueBy(periods, getMprPeriodKey);
}
function uniqueBy<T>(list: T[], keyOf: (value: T) => string): T[] {
  const seen = new Set<string>();
  return list.filter((value) => {
    const key = keyOf(value);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

/** Every item for every period, in the order the layout shows them; sortOrder is the index. */
export function getMprViewColumns(
  settings: Pick<IMprViewSettings, 'items' | 'periods' | 'layout'>
): MprColumnConfig[] {
  const items = uniqueMprItems(settings.items);
  let periods = uniqueMprPeriods(settings.periods);
  const columns: MprColumnConfig[] = [];
  if (settings.layout === MprLayout.item) {
    items.forEach((item) => periods.forEach((period) => columns.push(toMprColumnConfig(item, period))));
  } else {
    if (settings.layout === MprLayout.rows) {
      periods = periods.toSorted(mprChronologicalComparator);
    }
    periods.forEach((period) => items.forEach((item) => columns.push(toMprColumnConfig(item, period))));
  }
  return columns.map((column, i) => ({ ...column, sortOrder: i }));
}

export interface IMprPeriodBound {
  year: number;
  /** End month of the period: 1-12 for a month, 3/6/9/12 for a quarter, 12 for a year. */
  month: number;
}

/** The most periods one range adds; expandMprPeriodRange stops there. */
export const MaxMprRangeLength = 120;

/** How many periods a range holds, uncapped, so a picker can refuse one that would be cut. */
export function mprRangeLength(period: KpiPeriod, start: IMprPeriodBound, end: IMprPeriodBound): number {
  const step = period === KpiPeriod.year ? 12 : period === KpiPeriod.quarter ? 3 : 1;
  const snap = (month: number) => Math.ceil(month / step) * step;
  const span = end.year * 12 + snap(end.month) - (start.year * 12 + snap(start.month));
  return span < 0 ? 0 : Math.floor(span / step) + 1;
}

/** Every fixed period from start to end inclusive, at the given periodicity, up to MaxMprRangeLength.
 * Empty when end is before start. */
export function expandMprPeriodRange(
  period: KpiPeriod,
  start: IMprPeriodBound,
  end: IMprPeriodBound
): MprPeriod[] {
  const step = period === KpiPeriod.year ? 12 : period === KpiPeriod.quarter ? 3 : 1;
  // a bound inside a period means that period: May for a quarterly range is Q2
  const snap = (month: number) => Math.ceil(month / step) * step;
  const endIndex = end.year * 12 + snap(end.month);
  const result: MprPeriod[] = [];
  let year = start.year;
  let month = snap(start.month);
  while (year * 12 + month <= endIndex && result.length < MaxMprRangeLength) {
    result.push({ period, type: ReportingPeriodType.absolute, year, month });
    month += step;
    while (month > 12) {
      month -= 12;
      year += 1;
    }
  }
  return result;
}

export function isMprViewSettingsV1(settings: MprViewSettingsStored): settings is IMprViewSettingsV1 {
  return !('items' in settings);
}

/**
 * Views saved before FD-5844 hold the expanded column list and how it was sorted. The lists
 * are rebuilt from it in sort order; a view whose columns were not a full item x period
 * product gains the missing columns.
 */
export function migrateMprViewSettings(
  stored: MprViewSettingsStored,
  itemDisplayName?: (item: MprItem) => string
): IMprViewSettings {
  if (!isMprViewSettingsV1(stored)) {
    return { ...createMprViewSettings(), ...stored };
  }
  const columns = (stored.columns ?? []).toSorted(compareBySortOrder);
  const parts = columns.map(splitMprColumnConfig);
  let items = uniqueMprItems(parts.map(({ item }) => item));
  let periods = uniqueMprPeriods(parts.map(({ period }) => period));
  let layout: MprLayout = MprLayout.period;
  if (stored.columnSort) {
    periods = periods.toSorted(multiPeriodDateComparator);
    if (itemDisplayName) {
      items = items.toSorted((a, b) => itemDisplayName(a).localeCompare(itemDisplayName(b)));
    }
    layout = stored.columnSort === MprColumnSortType.displayName ? MprLayout.item : MprLayout.period;
  } else {
    // a custom order was usually the order the old form added columns in, item by item
    const identity = (column: MprColumnConfig) => {
      const { item, period } = splitMprColumnConfig(column);
      return `${getMprItemKey(item)}|${getMprPeriodKey(period)}`;
    };
    const saved = columns.map(identity).join();
    const matches = (candidate: MprLayout) =>
      getMprViewColumns({ items, periods, layout: candidate }).map(identity).join() === saved;
    if (matches(MprLayout.item) && !matches(MprLayout.period)) {
      layout = MprLayout.item;
    }
  }
  return createMprViewSettings({
    items,
    periods,
    layout,
    ...(stored.fundId != null ? { fundId: stored.fundId } : {}),
  });
}

// ---------- period picker options ----------

export const MonthValues = {
  [KpiPeriod.quarter]: [3, 6, 9, 12].map((month) => ({
    value: month,
    displayName: `Q${month / 3}`,
  })),
  [KpiPeriod.month]: MONTHS.map((month, index) => ({
    value: index + 1,
    displayName: month,
  })),
};

export const YearValues = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map((year) => ({
  value: year,
  displayName: String(year),
}));

const periodToRelativeDistances = {
  [KpiPeriod.month]: [0, 1, 2, 3, 4, 12, 24],
  [KpiPeriod.quarter]: [0, 1, 2, 3, 4, 8],
  [KpiPeriod.year]: [0, 1, 2, 3, 4],
};
export function getRelativeDistanceOptions(period?: KpiPeriod) {
  return (periodToRelativeDistances[period as KpiPeriod] ?? [0, 1, 2, 3, 4]).map((value) => ({
    value,
    displayName: value > 0 ? `Last Full ${period ?? 'Period'} - ${value}` : `Last Full ${period ?? 'Period'}`,
  }));
}
